import React, { createContext, useContext, useEffect, useState } from 'react';
import { discoveryCopy } from '../features/discovery/copy';

const words = {
  ko: {
    home: '홈',
    explore: '탐색',
    search: '검색',
    my: 'MY',
    albums: '앨범',
    reviews: '리뷰',
    playlists: '플리',
    feed: '포스트팝 피드',
    latestKr: '최신 앨범 · 국내',
    latestGlobal: '최신 앨범 · 해외',
    recommended: '포스트팝 추천',
    recentReviews: '최근 리뷰',
    recentPlaylists: '최근 플리',
    memberPlaylists: '회원들의 플레이리스트',
    albumsByEra: '연대별 앨범',
    timeline: '연대별 앨범',
    era: '연대',
    more: '전체 보기',
    loading: '불러오는 중',
    error: '불러오지 못했어요.',
    retry: '다시 시도',
    empty: '아직 표시할 내용이 없어요.',
    searchHint: '앨범, 아티스트, 리뷰, 플리 검색',
    discovery: '새로운 발견',
    searchIntro: '찾고 싶은 음악이나 회원을 검색해 보세요.',
    partialContent: '일부 연대의 앨범을 불러오지 못했어요.',
    recentSearches: '최근 검색',
    clearAll: '전체 삭제',
    removeSearch: '검색어 삭제',
    searchResultsFor: '다음에 대한 검색 결과',
    clear: '지우기',
    all: '전체',
    kr: '국내',
    global: '해외',
    year: '연도',
    month: '월',
    rediscovered: '다시 발견한 앨범',
    opening: '그 시절의 앨범',
    loadMore: '더 보기',
    navigation: '주요 메뉴',
    skip: '본문으로 건너뛰기',
    language: '언어',
    preview: '음악 플레이어',
    playPreview: '재생',
    pausePreview: '일시정지',
    nextPreview: '다음 곡',
    closePreview: '플레이어 닫기',
    previewProgress: '재생 진행',
    previewUnavailable: '미리듣기를 제공하지 않는 곡이에요.',
    previewBlocked: '재생 버튼을 다시 눌러 주세요.',
    previewError: '미리듣기를 불러오지 못했어요.',
    mediaLoading: '미디어를 불러오는 중이에요.',
    mediaError: '미디어를 불러오지 못했어요.',
    original: '원문 보기',
  },
  en: {
    home: 'Home',
    explore: 'Explore',
    search: 'Search',
    my: 'MY',
    albums: 'Albums',
    reviews: 'Reviews',
    playlists: 'Playlists',
    feed: 'Postpop Feed',
    latestKr: 'New releases · Korea',
    latestGlobal: 'New releases · Global',
    recommended: 'Postpop picks',
    recentReviews: 'Recent reviews',
    recentPlaylists: 'Recent playlists',
    memberPlaylists: 'Members’ playlists',
    albumsByEra: 'Albums by decade',
    timeline: 'Albums by decade',
    era: 'Decade',
    more: 'View all',
    loading: 'Loading',
    error: 'Couldn’t load this content.',
    retry: 'Try again',
    empty: 'Nothing here yet.',
    searchHint: 'Search albums, artists, reviews, and playlists',
    discovery: 'New discoveries',
    searchIntro: 'Search for music or members you want to find.',
    partialContent: 'Albums from some decades could not be loaded.',
    recentSearches: 'Recent searches',
    clearAll: 'Clear all',
    removeSearch: 'Remove search',
    searchResultsFor: 'Search results for',
    clear: 'Clear',
    all: 'All',
    kr: 'Korea',
    global: 'Global',
    year: 'Year',
    month: 'Month',
    rediscovered: 'Rediscovered albums',
    opening: 'Albums of the era',
    loadMore: 'Load more',
    navigation: 'Main navigation',
    skip: 'Skip to content',
    language: 'Language',
    preview: 'Music player',
    playPreview: 'Play',
    pausePreview: 'Pause',
    nextPreview: 'Next track',
    closePreview: 'Close player',
    previewProgress: 'Playback progress',
    previewUnavailable: 'No preview is available for this track.',
    previewBlocked: 'Press play again to start playback.',
    previewError: 'Couldn’t load this preview.',
    mediaLoading: 'Loading media.',
    mediaError: 'Couldn’t load this media.',
    original: 'View original',
  },
};
const translate = (locale, key, values = {}) =>
  String(words[locale]?.[key] || discoveryCopy[locale]?.[key] || words.ko[key] || key).replace(
    /\{\{(\w+)\}\}/g,
    (match, name) => values[name] ?? match,
  );
const Context = createContext({
  locale: 'ko',
  setLocale: () => {},
  t: (key) => translate('ko', key),
});

export function WebLocaleProvider({ children }) {
  const [locale, updateLocale] = useState(() => {
    try {
      return localStorage.getItem('POSTPOP_WEB_LOCALE') === 'en' ? 'en' : 'ko';
    } catch {
      return 'ko';
    }
  });
  const setLocale = (next) => {
    if (!['ko', 'en'].includes(next)) return;
    updateLocale(next);
    try {
      localStorage.setItem('POSTPOP_WEB_LOCALE', next);
    } catch {
      /* Session preference still works. */
    }
  };
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  return (
    <Context.Provider
      value={{ locale, setLocale, t: (key, values) => translate(locale, key, values) }}
    >
      {children}
    </Context.Provider>
  );
}
export const useWebLocale = () => useContext(Context);
